import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Loader2, Radio, Headphones, Sun, Moon } from "lucide-react";
import { api } from "@/lib/api";
import { ListenEngine } from "@/audio/ListenEngine";
import { Waveform } from "@/components/Waveform";
import { SpectrumAnalyzer } from "@/components/SpectrumAnalyzer";
import { LivePulse } from "@/components/LivePulse";
import { Slider } from "@/components/ui/slider";

function fmtUptime(s) {
  s = Math.floor(s || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h > 0 ? h + ":" : ""}${String(m).padStart(h > 0 ? 2 : 1, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function Listen() {
  const [status, setStatus] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(0.9);
  const [mode, setMode] = useState(null);
  const [isLightMode, setIsLightMode] = useState(() => localStorage.getItem("theme") === "light");
  const engineRef = useRef(null);

  useEffect(() => {
    if (isLightMode) {
      document.body.classList.add("light-mode");
      localStorage.setItem("theme", "light");
    } else {
      document.body.classList.remove("light-mode");
      localStorage.setItem("theme", "dark");
    }
  }, [isLightMode]);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/status");
      setStatus(data);
    } catch (e) { /* keep last */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    return () => { engineRef.current && engineRef.current.stop(); };
  }, []);

  const onAir = !!status?.on_air;

  const toggle = async () => {
    if (playing) {
      await engineRef.current?.stop();
      engineRef.current = null;
      setPlaying(false);
      setMode(null);
      return;
    }
    if (!onAir) return;
    setLoading(true);
    const engine = new ListenEngine({
      onControl: (m) => {
        if (m.type === "off_air") { engine.stop(); engineRef.current = null; setPlaying(false); poll(); }
        if (m.type === "meta" || m.type === "config") {
          setStatus((s) => ({ ...(s || {}), now_playing: m.now_playing ?? s?.now_playing, host_name: m.host_name ?? s?.host_name }));
        }
      },
      onState: (st, md) => {
        if (st === "playing") { setMode(md); setLoading(false); setPlaying(true); }
        if (st === "error") { setLoading(false); setPlaying(false); }
      },
    });
    engineRef.current = engine;
    try { await engine.play(volume); } catch (e) { setLoading(false); setPlaying(false); }
  };

  const changeVolume = (v) => {
    const nv = v[0] / 100;
    setVolume(nv);
    engineRef.current?.setVolume(nv);
  };

  const host = status?.host_name || "VOX-CAST";
  const title = status?.now_playing;
  const listeners = status?.listeners ?? 0;

  return (
    <div className="vox-noise relative min-h-screen w-full overflow-hidden flex flex-col" data-testid="listen-page">
      {/* ambient glow */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px] transition-opacity duration-1000"
        style={{ width: 520, height: 520, background: "#FF3B30", opacity: onAir ? 0.12 : 0.03 }}
      />

      {/* top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-6 sm:px-10">
        <div className="flex items-center gap-2 text-zinc-400">
          <Radio className="h-4 w-4 text-live" />
          <span className="font-mono text-xs tracking-[0.3em] uppercase">VOX·CAST</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsLightMode(!isLightMode)}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/10 transition-colors"
            title="Toggle High-Contrast Light Mode for Visually Impaired Users"
            data-testid="theme-toggle"
          >
            {isLightMode ? <Moon className="h-3.5 w-3.5 text-amber-500" /> : <Sun className="h-3.5 w-3.5 text-amber-400" />}
            <span className="font-mono text-[10px] uppercase font-semibold">{isLightMode ? "Dark" : "Light"}</span>
          </button>
          <Link
            to="/studio"
            data-testid="studio-link"
            className="font-mono text-xs tracking-widest uppercase text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            Studio →
          </Link>
        </div>
      </header>

      {/* center */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="vox-fade-up mb-6">
          <LivePulse active={onAir} />
        </div>

        <p className="vox-fade-up mb-3 font-mono text-xs uppercase tracking-[0.35em] text-zinc-500" style={{ animationDelay: "0.05s" }}>
          {onAir ? "Now Broadcasting" : "Currently Silent"}
        </p>

        <h1
          className="vox-fade-up font-serif tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl"
          style={{ animationDelay: "0.1s" }}
          data-testid="host-name"
        >
          {host}
        </h1>

        {title ? (
          <p className="vox-fade-up mt-6 max-w-2xl text-lg text-zinc-300 sm:text-xl" style={{ animationDelay: "0.15s" }} data-testid="now-playing">
            {title}
          </p>
        ) : (
          <p className="vox-fade-up mt-6 text-lg text-zinc-600" style={{ animationDelay: "0.15s" }} data-testid="now-playing">
            {onAir ? "Untitled session" : "The channel is off air. This page will update on its own."}
          </p>
        )}

        <div className="vox-fade-up mt-12 h-24 w-full max-w-xl p-2 rounded-xl bg-[#121214]/60 border border-white/5" style={{ animationDelay: "0.2s" }}>
          <SpectrumAnalyzer analyser={engineRef.current?.analyser} active={onAir && playing} bars={36} />
        </div>
      </main>

      {/* bottom controls */}
      <footer className="relative z-10 px-6 pb-10 sm:pb-14">
        <div className="mx-auto flex max-w-md flex-col items-center gap-6">
          <button
            onClick={toggle}
            disabled={(!onAir && !playing) || loading}
            data-testid="play-pause-btn"
            className="group relative flex h-20 w-20 items-center justify-center rounded-full transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              background: onAir ? "#FF3B30" : "#27272A",
              boxShadow: onAir ? "0 0 40px rgba(255,59,48,0.4)" : "none",
            }}
          >
            {loading ? (
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            ) : playing ? (
              <Pause className="h-8 w-8 text-white" fill="white" />
            ) : (
              <Play className="ml-1 h-8 w-8 text-white" fill="white" />
            )}
          </button>

          <div className="flex w-full items-center gap-3 px-4">
            {volume === 0 ? <VolumeX className="h-4 w-4 text-zinc-500" /> : <Volume2 className="h-4 w-4 text-zinc-500" />}
            <Slider
              value={[Math.round(volume * 100)]}
              onValueChange={changeVolume}
              max={100}
              step={1}
              className="flex-1"
              data-testid="volume-slider"
            />
          </div>

          <div className="flex items-center gap-6 font-mono text-xs uppercase tracking-widest text-zinc-500">
            <span className="flex items-center gap-1.5" data-testid="listener-count">
              <Headphones className="h-3.5 w-3.5" /> {listeners} {listeners === 1 ? "listener" : "listeners"}
            </span>
            {onAir && <span className="text-zinc-400">{fmtUptime(status?.uptime)}</span>}
            {mode === "ogg" && <span className="text-amber-400/80">fallback</span>}
          </div>
          <div className="text-[10px] font-mono text-zinc-600 tracking-wider">
            v1.8.1 (updated 2026-11-04 20:47)
          </div>
        </div>
      </footer>
    </div>
  );
}
